import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Save, Brain, BookOpen, Shield, Sparkles } from 'lucide-react';

export default function ClaretConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchConfig(); }, []);

  async function fetchConfig() {
    setLoading(true);
    try {
      const res = await apiClient.get('/claret/config');
      setConfig(res.data);
    } catch {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  }

  function update(key, value) {
    setConfig(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await apiClient.put('/claret/config', config);
      toast.success('Claret configuration saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="claret-config-page">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="claret-config-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Brain className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Claret Configuration</h1>
            <p className="text-muted-foreground text-sm">Persistent AI settings — stored in database, survives every deployment</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      {/* Custom Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-500" /> Custom Instructions</CardTitle>
          <CardDescription>
            These instructions are injected into every Claret conversation. Use this to teach Claret about your courses, pricing, USPs, sales strategies, or any business-specific knowledge. This persists across deployments.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={config?.custom_instructions || ''}
            onChange={e => update('custom_instructions', e.target.value)}
            placeholder={`Example:\n\nOur flagship courses:\n1. Professional Forex Trading - AED 4,999 (3 months, live trading floor access)\n2. Crypto Mastery - AED 3,499 (2 months, includes portfolio management)\n3. Stock Market Premium - AED 5,999 (4 months, US & UAE markets)\n\nKey USPs:\n- Only academy in UAE with a live trading floor\n- 1:1 mentorship ratio\n- Job placement assistance\n- Free demo class available\n\nCommon competitor weaknesses:\n- Delta Trading: No live floor, only online\n- FundFloat: Higher price, no placement support`}
            className="min-h-[250px] font-mono text-sm"
            data-testid="custom-instructions-textarea"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Tip: Include course details, pricing, USPs, competitor comparisons, sales scripts, and any FAQ answers. Claret will reference this in every conversation.
          </p>
        </CardContent>
      </Card>

      {/* Personality Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4 text-purple-500" /> Personality & Behavior Notes</CardTitle>
          <CardDescription>
            Fine-tune how Claret behaves. Add guardrails, tone preferences, or specific instructions for different scenarios.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={config?.personality_notes || ''}
            onChange={e => update('personality_notes', e.target.value)}
            placeholder={`Example:\n- Always encourage agents to book a demo class when customer is hesitant\n- Never mention competitor prices unless asked\n- When agent asks about discounts, remind them only TL can approve discounts above 10%\n- For UAE leads, emphasize the live trading floor as primary USP\n- For India leads, emphasize the online flexibility and installment options`}
            className="min-h-[150px] font-mono text-sm"
            data-testid="personality-notes-textarea"
          />
        </CardContent>
      </Card>

      {/* Feature Toggles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><BookOpen className="h-4 w-4 text-emerald-500" /> Feature Toggles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div>
              <p className="text-sm font-medium">Course Guidance</p>
              <p className="text-xs text-muted-foreground">Claret references uploaded course materials and custom instructions when guiding agents</p>
            </div>
            <Switch
              checked={config?.course_guidance_enabled !== false}
              onCheckedChange={v => update('course_guidance_enabled', v)}
              data-testid="course-guidance-toggle"
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div>
              <p className="text-sm font-medium">Objection Handling</p>
              <p className="text-xs text-muted-foreground">Claret injects relevant objection scripts when agents discuss customer objections</p>
            </div>
            <Switch
              checked={config?.objection_handling_enabled !== false}
              onCheckedChange={v => update('objection_handling_enabled', v)}
              data-testid="objection-handling-toggle"
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div>
              <p className="text-sm font-medium">Learn from History</p>
              <p className="text-xs text-muted-foreground">Claret remembers key points from past conversations to improve responses over time</p>
            </div>
            <Switch
              checked={config?.learning_from_history_enabled !== false}
              onCheckedChange={v => update('learning_from_history_enabled', v)}
              data-testid="learning-toggle"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
